import * as React from "react"
import {Divider, Form, Header, Segment} from "semantic-ui-react"
import {Place} from "./Place"
import {Journey} from "../model/places/PlaceTypes"
import {Journey as JOURNEY, UpdateJourney as UPDATE_JOURNEY} from "../graphql/queries"
import {GraphQL} from "../graphql"
import {ID} from "../model/id"
import {errorUnionToEither, handleError, newError, OrError} from "../graphql/errors"
import {history, WithModalStore} from "../App"
import {left} from "fp-ts/lib/Either"
import {none, Option, some} from "fp-ts/lib/Option"

function strictParseFloat(value: string): Option<number> {
  if (/^[-+]?([0-9]*(\.[0-9]*)?)$/.test(value)) {
    return some(Number(value))
  } else {
    return none
  }
}

function isValidKmString(distance: string): boolean {
  return kmStringToMetres(distance).isSome()
}

export function journeyDistanceToKmString(distance: number | null) {
  return distance !== null ? (distance / 1000).toString() : ""
}

function kmStringToMetres(distance: string): Option<number> {
  return strictParseFloat(distance).map((x) => Math.round(x * 1000))
}

interface Props extends WithModalStore {
  journey: Journey
}

interface State {
  distance: string
}

class JourneyForm extends React.Component<Props, State> {
  constructor(props: Props & WithModalStore) {
    super(props)
    this.state = {distance: journeyDistanceToKmString(props.journey.distance)}
  }

  render() {
    const {modalStore, journey} = this.props
    const {distance} = this.state
    const {origin, destination, waypoints} = journey
    return (
      <Segment.Group>
        <Segment inverted color="olive">
          <Form>
            <Header as="h3">Journey</Header>
            <Form.Field>
              <label>From</label>
              <Place modalStore={modalStore} place={origin} disabled={false} onDelete={null} />
            </Form.Field>
            <Form.Field>
              <label>To</label>
              <Place modalStore={modalStore} place={destination} disabled={false} onDelete={null} />
            </Form.Field>
            {waypoints.length > 0 && (
              <Form.Field>
                <label>Via</label>
                {waypoints.map((waypoint, index) => (
                  <Place
                    modalStore={modalStore}
                    key={index}
                    place={waypoint}
                    disabled={false}
                    onDelete={null}
                  />
                ))}
              </Form.Field>
            )}
            <Form.Field required>
              <label>Distance (km)</label>
              <Form.Input
                error={distance !== "" && !isValidKmString(distance)}
                placeholder={"Distance (km)"}
                value={distance}
                onChange={(event, {value}) => {
                  this.onDistance(value)
                }}
              />
            </Form.Field>
            <Divider />
            <Form.Group>
              <Form.Button
                positive
                children={"Save"}
                onClick={(e) => {
                  e.preventDefault()
                  modalStore.handlePromiseThrow(this.onSave())
                }}
                disabled={distance === "" || !isValidKmString(distance)}
              />
              <Form.Button
                children={"Reset"}
                onClick={(e) => {
                  e.preventDefault()
                  this.onReset()
                }}
              />
              <Form.Button
                children={"Back"}
                onClick={() => {
                  history.goBack()
                }}
              />
            </Form.Group>
          </Form>
        </Segment>
      </Segment.Group>
    )
  }

  onSave(): Promise<OrError<void>> {
    return this.props.modalStore.applyModal({
      pending: ["Saving journey", "Saving journey"],
      ok: () => ["Journey saved", "Journey saved"],
      error: (m) => ["Journey not saved", m],
      action: async () => {
        return this.onSaveRaw()
      },
    })
  }

  async onSaveRaw(): Promise<OrError<void>> {
    const {journey} = this.props
    const distanceM = kmStringToMetres(this.state.distance)

    if (distanceM.isNone()) {
      return left(newError(`Distance "${this.state.distance}" is invalid`))
    }

    const result = await GraphQL.mutate(UPDATE_JOURNEY, {
      id: journey.id,
      distance: distanceM.value,
    })
    return errorUnionToEither(result.updateJourney).map(() => void 0)
  }

  onDistance(distance: string) {
    this.setState({distance})
  }

  onReset() {
    const {journey} = this.props
    this.setState({distance: journeyDistanceToKmString(journey.distance)})
  }
}

function JourneyFormWithJourney(props: JourneyFormWithJourneyProps & WithModalStore) {
  const {journeyId} = props
  return (
    <GraphQL.Query
      query={JOURNEY}
      variables={{id: journeyId}}
      render={({data}) => {
        if (data) {
          return handleError(errorUnionToEither(data.journey), (journey) => (
            <JourneyForm modalStore={props.modalStore} journey={journey} />
          ))
        } else {
          return <div>Loading...</div>
        }
      }}
    />
  )
}

interface JourneyFormWithJourneyProps {
  journeyId: ID
}

export {JourneyFormWithJourney as JourneyPage}



// WEBPACK FOOTER //
// ./src/components/JourneyPage.tsx